import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTwitterCampaigns } from '../api';
import type { TwitterCampaignEntry } from '../api';
import { getApiErrorMessage } from 'lib/api';

const EMPTY: string[] = [];

const useTwitterCampaignsData = () => {
  useFormContext();
  const accountId = (useWatch({ name: 'accountId' }) as string | undefined) ?? '';
  const selectedCampaigns = (useWatch({ name: 'selectedCampaigns' }) as string[] | undefined) ?? EMPTY;
  const selectedOnly = (useWatch({ name: 'selectedCampaignsOnly' }) as boolean | undefined) ?? false;
  const filterByName = (useWatch({ name: 'filterCampaignsByName' }) as string[] | undefined) ?? EMPTY;

  const { data, isLoading, isError, error } = useTwitterCampaigns({ params: { accountId } });

  // Selected campaigns stay listed even when the name filter would hide them.
  const gridRows = useMemo<TwitterCampaignEntry[]>(() => {
    if (!data?.length) return [];

    const selected = new Set(selectedCampaigns);
    const matchesName = (row: TwitterCampaignEntry) => {
      if (!filterByName.length) return true;
      const name = row.campaign_name.toLowerCase();
      return filterByName.every((chip) => name.includes(chip.toLowerCase()));
    };

    return data
      .filter((row) => selected.has(row.campaign_id) || matchesName(row))
      .filter((row) => !selectedOnly || selected.has(row.campaign_id));
  }, [data, selectedCampaigns, selectedOnly, filterByName]);

  return {
    gridRows,
    totalCount: data?.length ?? 0,
    isLoading,
    isError,
    errorMessage: isError ? getApiErrorMessage(error, 'Failed to load campaigns') : undefined,
  };
};

export default useTwitterCampaignsData;
